import { isEqual, memoize } from 'lodash-es';

import { FavoriteFieldType } from 'core/graphql';

import {
  FavoriteField,
  getApplicableFavoriteFields,
} from '../FavoriteField/parsers';
import { getRecordSchemaVersion, RecordType } from '../Records';
import { emptyRecordValues } from '../Records/Record/schema/v3/RecordOverview/RecordProperty';

const FIELDS_KEY = 'fields';
export const FIELDS_PREFIX = `${FIELDS_KEY}.`;

/**
 * This function supports the Record itself and the nested `fields` property.
 */
const hasRecordValue = (data: RecordType, fieldName: string) =>
  data.hasOwnProperty(fieldName) &&
  (data[fieldName] || data[fieldName] === 0 || data[fieldName] === false) &&
  data[fieldName]?.length !== 0 &&
  !emptyRecordValues.includes(data[fieldName]);

export interface MatchedFavFieldValueType {
  value: any;
  record: RecordType;
  instanceCount: number;
}

export interface MatchedFavFieldType {
  [fieldName: string]: MatchedFavFieldValueType[];
}

export const parseFavoriteRecordFields = memoize(
  (records: RecordType[], favoriteFields: FavoriteField[]) => {
    // Iterate through all records.
    const multiRecordMatches: MatchedFavFieldType = {};
    records.forEach((record) => {
      // Verify the record is schema v3. If not, do not look for favorite fields.
      if (getRecordSchemaVersion(record) !== 'v3') {
        return null;
      }

      // Get the object type.
      const objectType = record.hasOwnProperty('objectType')
        ? (record.objectType as string)
        : undefined;

      // For each object type, get all associated favorite fields for that object
      // type, or all favorite fields that apply to all object types.
      const applicableFavoriteFields = getApplicableFavoriteFields(
        favoriteFields,
        FavoriteFieldType.Record,
        objectType
      ).map(({ fieldName }) => fieldName);

      const instantiateMultiRecordMatches = (favFieldName: string) => {
        // Instantiate the array if it doesn't exist already.
        if (!multiRecordMatches.hasOwnProperty(favFieldName)) {
          multiRecordMatches[favFieldName] = [];
        }
      };

      // For each matching favorite field, look in the record to see if it exists
      // and has a value.
      applicableFavoriteFields.forEach((favFieldName) => {
        // Handle nested properties under "fields" key. This is exposed to the
        // user as "extracted fields".
        if (favFieldName.startsWith(FIELDS_PREFIX)) {
          const fields =
            record.hasOwnProperty(FIELDS_KEY) && record[FIELDS_KEY];

          // Handle nested property under `fields`.
          const nestedFieldName = favFieldName.slice(FIELDS_PREFIX.length);
          if (hasRecordValue(fields, nestedFieldName)) {
            instantiateMultiRecordMatches(favFieldName);

            // Add the value to the list of matches for the current favorite
            // field.
            const value = record.fields[nestedFieldName];
            const match = { record, value, instanceCount: 1 };

            // A match exists when an identical value exists in the `fields` object.
            const existingMatchIndex = multiRecordMatches[
              favFieldName
            ].findIndex(({ value }) => match.value === value);
            if (existingMatchIndex !== -1) {
              multiRecordMatches[favFieldName][existingMatchIndex]
                .instanceCount++;
            } else {
              multiRecordMatches[favFieldName].push(match);
            }
          }
        }

        // Handle first-class properties.
        else if (hasRecordValue(record, favFieldName)) {
          instantiateMultiRecordMatches(favFieldName);

          const match = {
            record,
            value: record[favFieldName],
            instanceCount: 1,
          };

          // A match exists when an identical value exists.
          const existingMatchIndex = multiRecordMatches[favFieldName].findIndex(
            ({ value }) => isEqual(match.value, value)
          );
          if (existingMatchIndex !== -1) {
            multiRecordMatches[favFieldName][existingMatchIndex]
              .instanceCount++;
          } else {
            multiRecordMatches[favFieldName].push(match);
          }
        }
      });
    });

    return multiRecordMatches;
  }
);
